/**
 * <gallery-slider> — PDP main gallery. Extends the native scroll-snap base
 * <product-content-slider> (assets/product-slider.js) and reuses the theme's
 * product-slider structure/CSS (mobile scroll-indicator, columns). Adds:
 *   - arrows (theme .slider__arrow markup, data-gallery-prev/next) that
 *     DISABLE (not hide) at the ends
 *   - thumbnail sync (click thumb → scroll; scroll → active thumb[s]; on desktop
 *     the 2 visible slides are both active, styled as an underline in CSS)
 *   - fullscreen trigger (click a slide → dispatch `pdp-gallery:open` {index})
 *
 * Markup: .slider__track[data-slider-track] with .slider__item[data-gallery-slide][data-index];
 * [data-gallery-prev|next]; [data-gallery-thumb][data-index]. No Swiper.
 */
if (!customElements.get('gallery-slider')) {
  const Base = window.ProductContentSlider || HTMLElement;

  class GallerySlider extends Base {
    connectedCallback() {
      if (Base !== HTMLElement && super.connectedCallback) super.connectedCallback();

      this.gTrack = this.querySelector('[data-slider-track], .slider__track');
      if (!this.gTrack) return;
      this.slides = [...this.gTrack.querySelectorAll('[data-gallery-slide]')];
      this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
      this.thumbStrip = this.thumbs[0] && this.thumbs[0].parentElement;
      this.thumbPrev = this.querySelector('[data-thumb-prev]');
      this.thumbNext = this.querySelector('[data-thumb-next]');
      this.prevArrow = this.querySelector('[data-gallery-prev]');
      this.nextArrow = this.querySelector('[data-gallery-next]');
      this._index = 0;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.scrollToIndex(this.#idxOf(thumb)));
      });

      this.slides.forEach((slide) => {
        slide.addEventListener('click', (e) => {
          if (e.target.closest('a[href], video, button:not([data-gallery-slide])')) return;
          this.dispatchEvent(new CustomEvent('pdp-gallery:open', {
            bubbles: true,
            detail: { index: this.#idxOf(slide) },
          }));
        });
      });

      this.prevArrow?.addEventListener('click', () => this.#page(-1));
      this.nextArrow?.addEventListener('click', () => this.#page(1));

      // Thumbnail-strip arrows (revealed only when the strip overflows).
      this.thumbPrev?.addEventListener('click', () => this.#pageStrip(this.thumbStrip, -1));
      this.thumbNext?.addEventListener('click', () => this.#pageStrip(this.thumbStrip, 1));
      if (this.thumbStrip) {
        this._onThumbScroll = this.#throttle(
          () => this.#updateThumbNav(this.thumbStrip, this.thumbPrev, this.thumbNext), 80);
        this.thumbStrip.addEventListener('scroll', this._onThumbScroll, { passive: true });
      }

      // Fullscreen (Phase 2) — cloned from the inert <template> on first open.
      this.fsTemplate = this.querySelector('[data-fullscreen-template]');
      this.addEventListener('pdp-gallery:open', (e) => this.#openFullscreen(e.detail && e.detail.index || 0));

      this._onGScroll = this.#throttle(() => this.#onScroll(), 80);
      this.gTrack.addEventListener('scroll', this._onGScroll, { passive: true });
      this._onGResize = this.#throttle(() => this.#onScroll(), 150);
      window.addEventListener('resize', this._onGResize, { passive: true });
      this.#onScroll();
    }

    disconnectedCallback() {
      if (Base !== HTMLElement && super.disconnectedCallback) super.disconnectedCallback();
      if (this._onGScroll) this.gTrack?.removeEventListener('scroll', this._onGScroll);
      if (this._onGResize) window.removeEventListener('resize', this._onGResize);
      if (this._onThumbScroll) this.thumbStrip?.removeEventListener('scroll', this._onThumbScroll);
      // Color-change swaps the product HTML → this element disconnects. The fullscreen
      // overlay was appended to <body>, so remove it here or overlays pile up across
      // swaps. Only touch global scroll-lock state if this gallery's fullscreen was open.
      if (this.fsNode) {
        if (this.fsNode.classList.contains('is-open')) {
          document.documentElement.style.overflow = '';
          document.body.classList.remove('pdp-fs-open');
          document.removeEventListener('keydown', this._fsEsc);
        }
        this.fsNode.remove();
        this.fsNode = null;
      }
    }

    #idxOf(el) {
      const i = parseInt(el.getAttribute('data-index'), 10);
      return Number.isNaN(i) ? 0 : i;
    }

    #slideStep() {
      if (!this.slides.length) return this.gTrack.clientWidth || 1;
      const gap = parseFloat(getComputedStyle(this.gTrack).columnGap) || 0;
      return this.slides[0].offsetWidth + gap;
    }

    #visibleCount() {
      const step = this.#slideStep();
      return Math.max(1, Math.round(this.gTrack.clientWidth / step));
    }

    scrollToIndex(index) {
      const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
      this.gTrack.scrollTo({ left: clamped * this.#slideStep(), behavior: 'smooth' });
    }

    #page(dir) {
      this.gTrack.scrollBy({ left: dir * this.gTrack.clientWidth, behavior: 'smooth' });
    }

    #onScroll() {
      const step = this.#slideStep();
      const index = step > 0 ? Math.round(this.gTrack.scrollLeft / step) : 0;
      if (index !== this._index) this._index = index;
      this.#setActiveThumbs(index, this.#visibleCount());
      this.#updateArrows();
      this.#updateThumbNav(this.thumbStrip, this.thumbPrev, this.thumbNext);
    }

    #setActiveThumbs(start, count) {
      const end = start + count - 1;
      let firstActive = null;
      this.thumbs.forEach((t) => {
        const i = this.#idxOf(t);
        const on = i >= start && i <= end;
        t.classList.toggle('is-active', on);
        if (on && !firstActive) firstActive = t;
      });
      // Keep the active thumb in view when the strip overflows (auto-advance /
      // auto-scroll-back as the main slider navigates).
      this.#ensureThumbVisible(this.thumbStrip, firstActive);
    }

    /* Scroll ONLY the thumb strip (never the page) so `thumb` is fully visible.
       Direction-agnostic: nudges whichever axis the strip actually scrolls. */
    #ensureThumbVisible(container, thumb) {
      if (!container || !thumb) return;
      const c = container.getBoundingClientRect();
      const t = thumb.getBoundingClientRect();
      const pad = 8;
      if (container.scrollHeight > container.clientHeight + 1) {
        if (t.top < c.top) container.scrollBy({ top: t.top - c.top - pad, behavior: 'smooth' });
        else if (t.bottom > c.bottom) container.scrollBy({ top: t.bottom - c.bottom + pad, behavior: 'smooth' });
      }
      if (container.scrollWidth > container.clientWidth + 1) {
        if (t.left < c.left) container.scrollBy({ left: t.left - c.left - pad, behavior: 'smooth' });
        else if (t.right > c.right) container.scrollBy({ left: t.right - c.right + pad, behavior: 'smooth' });
      }
    }

    /* Page the thumb strip by ~80% of its viewport, along whichever axis it
       scrolls (vertical for the desktop fullscreen column, horizontal else). */
    #pageStrip(strip, dir) {
      if (!strip) return;
      if (strip.scrollHeight > strip.clientHeight + 1) {
        strip.scrollBy({ top: dir * strip.clientHeight * 0.8, behavior: 'smooth' });
      } else {
        strip.scrollBy({ left: dir * strip.clientWidth * 0.8, behavior: 'smooth' });
      }
    }

    /* Reveal the strip arrows only when it overflows; disable at the ends. */
    #updateThumbNav(strip, prev, next) {
      if (!strip || (!prev && !next)) return;
      const vertical = strip.scrollHeight > strip.clientHeight + 1;
      const horizontal = strip.scrollWidth > strip.clientWidth + 1;
      const overflow = vertical || horizontal;
      if (prev) prev.hidden = !overflow;
      if (next) next.hidden = !overflow;
      if (!overflow) return;
      const pos = vertical ? strip.scrollTop : strip.scrollLeft;
      const max = vertical
        ? strip.scrollHeight - strip.clientHeight
        : strip.scrollWidth - strip.clientWidth;
      if (prev) prev.disabled = pos <= 1;
      if (next) next.disabled = pos >= max - 1;
    }

    #updateArrows() {
      const max = this.gTrack.scrollWidth - this.gTrack.clientWidth;
      const x = this.gTrack.scrollLeft;
      if (this.prevArrow) this.prevArrow.disabled = x <= 1;
      if (this.nextArrow) this.nextArrow.disabled = x >= max - 1;
    }

    /* ── Fullscreen ───────────────────────────────────────────────── */
    #openFullscreen(index) {
      if (!this.fsTemplate) return;
      if (!this.fsNode) this.#buildFullscreen();
      if (!this.fsNode) return;
      this.fsNode.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      document.body.classList.add('pdp-fs-open');
      document.addEventListener('keydown', this._fsEsc);
      this.#fsScrollToIndex(index, true);
      this.#fsSetActiveThumb(index);
      requestAnimationFrame(() =>
        this.#updateThumbNav(this.fsThumbStrip, this.fsThumbPrev, this.fsThumbNext));
    }

    #buildFullscreen() {
      const node = this.fsTemplate.content.cloneNode(true).querySelector('.pdp-fs');
      if (!node) return;
      document.body.appendChild(node);
      this.fsNode = node;
      this.fsTrack = node.querySelector('[data-fs-track]');
      this.fsThumbs = [...node.querySelectorAll('[data-fs-thumb]')];
      this.fsThumbStrip = this.fsThumbs[0] && this.fsThumbs[0].parentElement;
      this.fsThumbPrev = node.querySelector('[data-thumb-prev]');
      this.fsThumbNext = node.querySelector('[data-thumb-next]');
      this._fsEsc = (e) => { if (e.key === 'Escape') this.#closeFullscreen(); };

      node.querySelector('[data-fs-close]')?.addEventListener('click', () => this.#closeFullscreen());
      node.addEventListener('click', (e) => { if (e.target === node) this.#closeFullscreen(); });

      this.fsThumbs.forEach((t) => t.addEventListener('click', () => {
        this.#fsResetZoom();
        this.#fsScrollToIndex(this.#idxOf(t), false);
      }));

      this.fsThumbPrev?.addEventListener('click', () => this.#pageStrip(this.fsThumbStrip, -1));
      this.fsThumbNext?.addEventListener('click', () => this.#pageStrip(this.fsThumbStrip, 1));
      if (this.fsThumbStrip) {
        this._onFsThumbScroll = this.#throttle(
          () => this.#updateThumbNav(this.fsThumbStrip, this.fsThumbPrev, this.fsThumbNext), 80);
        this.fsThumbStrip.addEventListener('scroll', this._onFsThumbScroll, { passive: true });
      }

      this._fsOnScroll = this.#throttle(() => this.#fsOnScroll(), 80);
      this.fsTrack?.addEventListener('scroll', this._fsOnScroll, { passive: true });

      node.addEventListener('zoom:start', () => node.classList.add('is-zoomed'));
      node.addEventListener('zoom:end', () => node.classList.remove('is-zoomed'));
    }

    #closeFullscreen() {
      this.#fsResetZoom();
      this.fsNode?.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      document.body.classList.remove('pdp-fs-open');
      document.removeEventListener('keydown', this._fsEsc);
    }

    #fsResetZoom() {
      this.fsNode?.querySelectorAll('pinch-zoom-image').forEach((z) => z.resetZoom && z.resetZoom());
      this.fsNode?.classList.remove('is-zoomed');
    }

    // Desktop fullscreen is a VERTICAL slider (CSS flips the track to a column at
    // ≥768px); mobile stays horizontal. Drive whichever axis the track scrolls.
    #fsVertical() {
      return !!this.fsTrack && getComputedStyle(this.fsTrack).flexDirection === 'column';
    }

    #fsScrollToIndex(index, instant) {
      if (!this.fsTrack) return;
      const behavior = instant ? 'auto' : 'smooth';
      if (this.#fsVertical()) {
        this.fsTrack.scrollTo({ top: index * this.fsTrack.clientHeight, behavior });
      } else {
        this.fsTrack.scrollTo({ left: index * this.fsTrack.clientWidth, behavior });
      }
    }

    #fsOnScroll() {
      if (!this.fsTrack) return;
      const index = this.#fsVertical()
        ? Math.round(this.fsTrack.scrollTop / (this.fsTrack.clientHeight || 1))
        : Math.round(this.fsTrack.scrollLeft / (this.fsTrack.clientWidth || 1));
      this.#fsSetActiveThumb(index);
    }

    #fsSetActiveThumb(index) {
      let active = null;
      this.fsThumbs?.forEach((t) => {
        const on = this.#idxOf(t) === index;
        t.classList.toggle('is-active', on);
        if (on) active = t;
      });
      this.#ensureThumbVisible(this.fsThumbStrip, active);
    }

    #throttle(fn, wait) {
      let last = 0, scheduled = false;
      return () => {
        const now = Date.now();
        if (now - last >= wait) { last = now; fn(); }
        else if (!scheduled) {
          scheduled = true;
          setTimeout(() => { scheduled = false; last = Date.now(); fn(); }, wait - (now - last));
        }
      };
    }
  }

  customElements.define('gallery-slider', GallerySlider);
}
